import { createContext, useCallback, useContext, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, HelpCircle } from "lucide-react";
import { useModalBack } from "@/hooks/useModalBack";

const ConfirmContext = createContext(null);

/**
 * useConfirm() returns an async function:
 *   const ok = await confirm({ title, description, confirmLabel, cancelLabel, danger });
 * It resolves to `true` if the user confirms, `false` otherwise.
 */
export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error("useConfirm must be used inside <ConfirmDialogProvider>");
  }
  return ctx;
}

export function ConfirmDialogProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState({});
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    setOpts(options);
    setOpen(true);
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const finish = (result) => {
    setOpen(false);
    const r = resolverRef.current;
    resolverRef.current = null;
    if (r) r(result);
  };

  useModalBack(open, () => finish(false));

  const danger = !!opts.danger;
  const Icon = danger ? AlertTriangle : HelpCircle;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={open} onOpenChange={(v) => (v ? null : finish(false))}>
        <AlertDialogContent
          className="border-white/10 bg-[#0b0b0f] p-0 text-white shadow-[0_20px_60px_-15px_rgba(26,63,209,0.35)] sm:max-w-md"
          data-testid="confirm-dialog"
        >
          <div className="flex items-start gap-4 border-b border-white/10 p-5">
            <div
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                danger
                  ? "bg-red-500/10 text-red-400 ring-1 ring-red-500/30"
                  : "bg-[#1a3fd1]/15 text-[#8fa4f0] ring-1 ring-[#1a3fd1]/40"
              }`}
            >
              <Icon className="h-5 w-5" />
            </div>
            <AlertDialogHeader className="min-w-0 flex-1 space-y-1 text-left">
              <AlertDialogTitle
                className="text-lg font-semibold text-white"
                data-testid="confirm-dialog-title"
              >
                {opts.title || "Are you sure?"}
              </AlertDialogTitle>
              {opts.description ? (
                <AlertDialogDescription
                  className="text-sm leading-relaxed text-white/60"
                  data-testid="confirm-dialog-description"
                >
                  {opts.description}
                </AlertDialogDescription>
              ) : null}
            </AlertDialogHeader>
          </div>

          <AlertDialogFooter className="flex-row justify-end gap-2 border-t border-white/5 bg-black/20 p-4 sm:justify-end">
            <AlertDialogCancel
              onClick={() => finish(false)}
              data-testid="confirm-dialog-cancel"
              className="mt-0 border-white/15 bg-transparent text-sm text-white/80 hover:bg-white/10 hover:text-white"
            >
              {opts.cancelLabel || "Cancel"}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => finish(true)}
              data-testid="confirm-dialog-confirm"
              className={
                danger
                  ? "bg-red-600 text-sm text-white hover:bg-red-500"
                  : "bg-[#1a3fd1] text-sm text-white hover:bg-[#1a3fd1]/85"
              }
            >
              {opts.confirmLabel || (danger ? "Delete" : "Confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}
